import React from 'react';
import { Popup } from 'semantic-ui-react';
import { apiGet } from '../../services/apiClient';
import { toast } from 'react-toastify';
import CarrierReviewModal from '../../modals/carrierReviewModal';
import StarRating, { PRIMARY_BLUE } from './StarRating';
import './carrierReview.scss';

const TOOLTIP_NO_REVIEW =
    'Ваша оценка работы ТК по доставке. Нажмите, чтобы оценить.\nТак же Вы сможете оставить отзыв о работе ТК по данной посылке.';

const TOOLTIP_HAS_REVIEW =
    'Ваша оценка работы ТК по доставке. Нажмите, чтобы изменить.\nТак же Вы сможете оставить или дополнить отзыв о работе ТК по данной посылке.';

/**
 * Рейтинг ТК под логотипом:
 * - серые пустые звёзды, если отзыва нет
 * - синие звёзды с оценкой, если отзыв есть
 * - клик подгружает данные и только затем открывает диалог
 */
export default class CarrierReviewRating extends React.Component {
    constructor(args) {
        super(args);

        this._isMounted = false;
        this.state = {
            loading: false,
            openingModal: false,
            modalOpen: false,
            rating: this.props.initialRating || 0,
            hasReview: !!(this.props.initialRating && this.props.initialRating > 0),
            modalRating: 0,
            modalComment: '',
            modalReviewId: null,
            modalAuthorLogin: null,
            modalAuthorCreatedAt: null,
            modalParcel: this.props.parcel || null
        };
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.parcelId && this.props.initialRating === undefined) {
            this.loadReviewSummary();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.parcelId !== this.props.parcelId && this.props.parcelId) {
            if (this.props.initialRating === undefined) {
                this.loadReviewSummary();
            } else {
                this.setState({
                    rating: this.props.initialRating || 0,
                    hasReview: !!(this.props.initialRating && this.props.initialRating > 0)
                });
            }
        } else if (prevProps.initialRating !== this.props.initialRating
            && this.props.initialRating !== undefined) {
            this.setState({
                rating: this.props.initialRating || 0,
                hasReview: !!(this.props.initialRating && this.props.initialRating > 0)
            });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    loadReviewSummary = () => {
        const { parcelId } = this.props;
        if (!parcelId) {
            return;
        }

        this.setState({ loading: true });
        apiGet(`carriers/reviews/parcel/${parcelId}`)
            .then((data) => {
                if (!this._isMounted) {
                    return;
                }
                const rating = data && data.rating ? data.rating : 0;
                this.setState({
                    loading: false,
                    rating,
                    hasReview: !!(data && data.id)
                });
            })
            .catch(() => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({ loading: false, rating: 0, hasReview: false });
            });
    };

    openModal = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const { parcelId, parcel } = this.props;
        if (!parcelId || this.state.openingModal) {
            return;
        }

        this.setState({ openingModal: true });

        const reviewPromise = apiGet(`carriers/reviews/parcel/${parcelId}`);
        const parcelPromise = parcel
            ? Promise.resolve(parcel)
            : apiGet(`parcels/${parcelId}`);

        Promise.all([reviewPromise, parcelPromise])
            .then(([data, parcelData]) => {
                if (!this._isMounted) {
                    return;
                }
                const rating = data && data.rating ? data.rating : 0;
                this.setState({
                    openingModal: false,
                    modalOpen: true,
                    rating,
                    hasReview: !!(data && data.id),
                    modalRating: rating,
                    modalComment: (data && data.comment) || '',
                    modalReviewId: (data && data.id) || null,
                    modalAuthorLogin: (data && data.userLogin) || null,
                    modalAuthorCreatedAt: (data && data.createdAt) || null,
                    modalParcel: parcelData || null
                });
            })
            .catch((err) => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({ openingModal: false });
                const errorText = (err && err.error) || 'Не удалось загрузить данные';
                toast.error(errorText);
            });
    };

    closeModal = () => {
        this.setState({ modalOpen: false, modalParcel: this.props.parcel || null });
    };

    handleSaved = (data) => {
        const rating = data && data.rating ? data.rating : 0;
        this.setState({
            rating,
            hasReview: !!(data && data.id),
            modalRating: rating,
            modalComment: (data && data.comment) || '',
            modalReviewId: (data && data.id) || null,
            modalAuthorLogin: (data && data.userLogin) || null,
            modalAuthorCreatedAt: (data && data.createdAt) || null
        });
        if (this.props.onChanged) {
            this.props.onChanged(data);
        }
    };

    render() {
        const { parcelId, starDimension = '14px', starSpacing = '1px' } = this.props;
        const {
            modalOpen, openingModal, rating, hasReview, modalRating, modalComment,
            modalReviewId, modalAuthorLogin, modalAuthorCreatedAt, modalParcel
        } = this.state;

        if (!parcelId) {
            return null;
        }

        const tooltipText = hasReview ? TOOLTIP_HAS_REVIEW : TOOLTIP_NO_REVIEW;

        return (
            <React.Fragment>
                <Popup
                    inverted
                    size="mini"
                    position="bottom center"
                    style={{ maxWidth: 280 }}
                    content={<div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}
                    trigger={
                        <button
                            type="button"
                            className={`carrier-review-rating${openingModal ? ' carrier-review-rating--loading' : ''}`}
                            onClick={this.openModal}
                            disabled={openingModal}
                        >
                            <StarRating
                                rating={hasReview ? rating : 0}
                                readOnly
                                starRatedColor={PRIMARY_BLUE}
                                starDimension={starDimension}
                                starSpacing={starSpacing}
                                name={`carrier-review-rating-${parcelId}`}
                            />
                        </button>
                    }
                />
                <CarrierReviewModal
                    open={modalOpen}
                    parcelId={parcelId}
                    parcel={modalParcel}
                    initialRating={modalRating}
                    initialComment={modalComment}
                    reviewId={modalReviewId}
                    authorLogin={modalAuthorLogin}
                    authorCreatedAt={modalAuthorCreatedAt}
                    onClose={this.closeModal}
                    onSaved={this.handleSaved}
                />
            </React.Fragment>
        );
    }
}



// WEBPACK FOOTER //
// ./App/components/CarrierReview/CarrierReviewRating.jsx